import { AlertTriangle } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { useDeleteAdmin } from '../../hooks/queries/useAdminQueries';
import { IBackofficeAdmin } from '../../types';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface IAdminDeleteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  admin: IBackofficeAdmin | null;
}

const AdminDeleteDialog = ({
  isOpen,
  onClose,
  admin,
}: IAdminDeleteDialogProps) => {
  const { t } = useTranslation();
  const deleteAdmin = useDeleteAdmin();

  const handleConfirm = async () => {
    if (!admin) return;

    await deleteAdmin.mutateAsync(admin.id);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{t('admin.delete_title')}</DialogTitle>
          <DialogDescription>
            {t('admin.delete_confirm', { name: admin?.displayName ?? '' })}
          </DialogDescription>
        </DialogHeader>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-medium text-red-700 mb-1">
                {t('admin.delete_warning')}
              </div>
              <div className="text-xs text-red-600">
                {t('admin.delete_warning_text')}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={deleteAdmin.isPending}
          >
            {deleteAdmin.isPending
              ? t('common.saving')
              : t('admin.confirm_delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminDeleteDialog;
